import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.ts';
import { bump } from '../signal.ts';
import { show, showError } from '../toast.ts';
import { kcal, MEAL_LABEL, shiftDate } from '../format.ts';
import type { Entry, Food, FoodHit, Macros, Meal } from '../../shared/types.ts';
import { Sheet } from './Sheet.tsx';

type Tab = 'search' | 'scan' | 'quick' | 'new';
/** Something that can be logged: a saved food, or an Open Food Facts result. */
type Pick = { food: Food } | { hit: FoodHit };

function logged(e: Entry) {
  bump();
  show(`Logged ${e.name}`, {
    action: { label: 'Undo', run: () => api('/api/entries/delete', { body: { ids: [e.id] } }).then(bump, showError) },
  });
}

export function AddSheet({ date, meal, onClose }: { date: string; meal: Meal; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('search');
  const [pick, setPick] = useState<Pick | null>(null);

  return (
    <Sheet title={`Add to ${MEAL_LABEL[meal]}`} onClose={onClose}>
      {pick ? (
        <Serving pick={pick} date={date} meal={meal} onBack={() => setPick(null)} onDone={onClose} />
      ) : (
        <>
          <div class="segmented">
            {(['search', 'scan', 'quick', 'new'] as const).map((t) => (
              <button key={t} class={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
                {{ search: 'Search', scan: 'Barcode', quick: 'Quick add', new: 'New food' }[t]}
              </button>
            ))}
          </div>
          {tab === 'search' && <Search date={date} meal={meal} onPick={setPick} onDone={onClose} />}
          {tab === 'scan' && <Scan onPick={setPick} />}
          {tab === 'quick' && <QuickAdd date={date} meal={meal} onDone={onClose} />}
          {tab === 'new' && <NewFood onSaved={(food) => setPick({ food })} />}
        </>
      )}
    </Sheet>
  );
}

function Search({ date, meal, onPick, onDone }: { date: string; meal: Meal; onPick: (p: Pick) => void; onDone: () => void }) {
  const [q, setQ] = useState('');
  const [foods, setFoods] = useState<Food[]>([]);
  const [hits, setHits] = useState<FoodHit[] | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api<Food[]>(`/api/foods?q=${encodeURIComponent(q)}`).then(setFoods, showError);
    }, 150);
    setHits(null);
    return () => clearTimeout(t);
  }, [q]);

  async function searchOff(e: Event) {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      setHits(await api<FoodHit[]>(`/api/off/search?q=${encodeURIComponent(q.trim())}`));
    } catch (err) {
      showError(err);
    } finally {
      setSearching(false);
    }
  }

  async function copyYesterday() {
    try {
      const out = await api<Entry[]>('/api/entries/copy', { body: { from: shiftDate(date, -1), to: date, meal } });
      if (!out.length) return show(`Nothing logged for ${MEAL_LABEL[meal].toLowerCase()} yesterday`);
      bump();
      const ids = out.map((e) => e.id);
      show(`Copied ${out.length} item${out.length > 1 ? 's' : ''} from yesterday`, {
        action: { label: 'Undo', run: () => api('/api/entries/delete', { body: { ids } }).then(bump, showError) },
      });
      onDone();
    } catch (err) {
      showError(err);
    }
  }

  return (
    <div class="search">
      <form onSubmit={searchOff} class="row">
        <input type="search" value={q} placeholder="Search your foods" onInput={(e) => setQ(e.currentTarget.value)} autoFocus />
      </form>
      {!q && (
        <button class="link" onClick={copyYesterday}>Copy yesterday’s {MEAL_LABEL[meal].toLowerCase()}</button>
      )}
      <ul class="results">
        {foods.map((f) => (
          <li key={f.id}>
            <button onClick={() => onPick({ food: f })}>
              <span class="name">
                {f.name}
                <small class="muted">{[f.brand, f.serving].filter(Boolean).join(' · ')}</small>
              </span>
              <span class="nums">{kcal(f.kcal)}</span>
            </button>
          </li>
        ))}
      </ul>
      {q.trim().length >= 2 && hits === null && (
        <button class="ghost wide" onClick={searchOff} disabled={searching}>
          {searching ? 'Searching…' : `Search Open Food Facts for “${q.trim()}”`}
        </button>
      )}
      {hits && (
        <>
          <h3 class="muted">Open Food Facts</h3>
          {!hits.length && <p class="muted">No matches. Try the AI box, or add it as a new food.</p>}
          <ul class="results">
            {hits.map((h, i) => (
              <li key={(h.barcode ?? '') + i}>
                <button onClick={() => onPick({ hit: h })}>
                  <span class="name">
                    {h.name}
                    <small class="muted">{[h.brand, h.serving].filter(Boolean).join(' · ')}</small>
                  </span>
                  <span class="nums">{kcal(h.kcal)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** How much of a picked food, then log it. */
function Serving({ pick, date, meal, onBack, onDone }: {
  pick: Pick; date: string; meal: Meal; onBack: () => void; onDone: () => void;
}) {
  const base = 'food' in pick ? pick.food : pick.hit;
  const per100 = 'hit' in pick ? pick.hit.per100g : null;
  const gramsPerServing = base.servingGrams;
  const [mode, setMode] = useState<'servings' | 'grams'>('servings');
  const [amount, setAmount] = useState('1');
  const [busy, setBusy] = useState(false);

  const n = Number(amount.replace(',', '.')) || 0;
  const servings = mode === 'servings' ? n : gramsPerServing ? n / gramsPerServing : 0;
  const scaled = (k: keyof Macros) =>
    mode === 'grams' && per100 ? (per100[k] * n) / 100 : base[k] * servings;

  async function log() {
    if (!(servings > 0)) return;
    setBusy(true);
    try {
      const food =
        'food' in pick
          ? pick.food
          : await api<Food>('/api/foods', {
              body: {
                name: pick.hit.name, brand: pick.hit.brand, serving: pick.hit.serving, servingGrams: pick.hit.servingGrams,
                barcode: pick.hit.barcode, kcal: pick.hit.kcal, protein: pick.hit.protein, carbs: pick.hit.carbs,
                fat: pick.hit.fat, fiber: pick.hit.fiber, source: 'off',
              },
            });
      logged(await api<Entry>('/api/entries', { body: { date, meal, foodId: food.id, servings } }));
      onDone();
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="serving">
      <button class="link" onClick={onBack}>‹ Back</button>
      <h3>{base.name}</h3>
      <p class="muted">{[base.brand, `1 serving = ${base.serving}`].filter(Boolean).join(' · ')}</p>
      <div class="row">
        <input inputMode="decimal" value={amount} onInput={(e) => setAmount(e.currentTarget.value)} autoFocus aria-label="Amount" />
        {gramsPerServing ? (
          <div class="segmented small">
            <button class={mode === 'servings' ? 'on' : ''} onClick={() => { setMode('servings'); setAmount('1'); }}>servings</button>
            <button class={mode === 'grams' ? 'on' : ''} onClick={() => { setMode('grams'); setAmount(String(Math.round(gramsPerServing))); }}>g</button>
          </div>
        ) : (
          <span class="muted">servings</span>
        )}
      </div>
      <dl class="macrogrid">
        <div><dt>kcal</dt><dd>{kcal(scaled('kcal'))}</dd></div>
        <div><dt>Protein</dt><dd>{Math.round(scaled('protein'))} g</dd></div>
        <div><dt>Carbs</dt><dd>{Math.round(scaled('carbs'))} g</dd></div>
        <div><dt>Fat</dt><dd>{Math.round(scaled('fat'))} g</dd></div>
      </dl>
      <button class="primary wide" onClick={log} disabled={busy || !(servings > 0)}>Log</button>
    </div>
  );
}

declare class BarcodeDetector {
  constructor(opts?: { formats: string[] });
  detect(src: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

function Scan({ onPick }: { onPick: (p: Pick) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');
  const supported = typeof (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector === 'function';

  async function lookup(c: string) {
    setStatus(`Looking up ${c}…`);
    try {
      const r = await api<{ food: Food | null; hit: FoodHit | null }>(`/api/barcode/${c}`);
      if (r.food) onPick({ food: r.food });
      else if (r.hit) onPick({ hit: r.hit });
      else setStatus(`${c} isn’t in Open Food Facts. Photograph the label for the AI box, or add it as a new food.`);
    } catch (err) {
      setStatus('');
      showError(err);
    }
  }

  useEffect(() => {
    if (!supported) return;
    let stream: MediaStream | null = null;
    let stop = false;
    const detector = new BarcodeDetector({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e'] });
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (stop) return stream.getTracks().forEach((t) => t.stop());
        video.current!.srcObject = stream;
        await video.current!.play();
        while (!stop) {
          const found = await detector.detect(video.current!).catch(() => []);
          if (found[0]) {
            stop = true;
            await lookup(found[0].rawValue);
            break;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch {
        setStatus('No camera access. Type the number under the barcode instead.');
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div class="scan">
      {supported ? <video ref={video} playsInline muted /> : <p class="muted">This browser can’t read barcodes from the camera; type the number instead.</p>}
      <form
        class="row"
        onSubmit={(e) => {
          e.preventDefault();
          const c = code.replace(/\D/g, '');
          if (c.length >= 6) lookup(c);
        }}
      >
        <input inputMode="numeric" value={code} placeholder="Barcode number" onInput={(e) => setCode(e.currentTarget.value)} />
        <button class="ghost">Look up</button>
      </form>
      {status && <p class="muted">{status}</p>}
    </div>
  );
}

function MacroInputs({ v, set }: { v: Record<string, string>; set: (k: string, val: string) => void }) {
  return (
    <div class="macroinputs">
      {(['kcal', 'protein', 'carbs', 'fat'] as const).map((k) => (
        <label key={k}>
          <span>{k === 'kcal' ? 'Calories' : `${k[0].toUpperCase()}${k.slice(1)} (g)`}</span>
          <input inputMode="decimal" value={v[k] ?? ''} onInput={(e) => set(k, e.currentTarget.value)} />
        </label>
      ))}
    </div>
  );
}

const toNum = (s: string | undefined) => Number((s ?? '').replace(',', '.')) || 0;

function QuickAdd({ date, meal, onDone }: { date: string; meal: Meal; onDone: () => void }) {
  const [v, setV] = useState<Record<string, string>>({});
  const set = (k: string, val: string) => setV((o) => ({ ...o, [k]: val }));
  async function save(e: Event) {
    e.preventDefault();
    const p = toNum(v.protein), c = toNum(v.carbs), f = toNum(v.fat);
    // Calories default to what the macros add up to.
    const k = v.kcal ? toNum(v.kcal) : p * 4 + c * 4 + f * 9;
    if (!(k > 0)) return showError('Enter calories or macros');
    try {
      logged(await api<Entry>('/api/entries', {
        body: { date, meal, name: v.name?.trim() || 'Quick add', kcal: k, protein: p, carbs: c, fat: f },
      }));
      onDone();
    } catch (err) {
      showError(err);
    }
  }
  return (
    <form onSubmit={save} class="form">
      <label><span>Name (optional)</span><input value={v.name ?? ''} onInput={(e) => set('name', e.currentTarget.value)} /></label>
      <MacroInputs v={v} set={set} />
      <button class="primary wide">Log</button>
    </form>
  );
}

function NewFood({ onSaved }: { onSaved: (f: Food) => void }) {
  const [v, setV] = useState<Record<string, string>>({ serving: '1 serving' });
  const set = (k: string, val: string) => setV((o) => ({ ...o, [k]: val }));
  async function save(e: Event) {
    e.preventDefault();
    if (!v.name?.trim()) return showError('Name the food');
    const p = toNum(v.protein), c = toNum(v.carbs), f = toNum(v.fat);
    try {
      onSaved(await api<Food>('/api/foods', {
        body: {
          name: v.name, brand: v.brand ?? '', serving: v.serving || '1 serving',
          servingGrams: v.grams ? toNum(v.grams) : null,
          barcode: v.barcode?.replace(/\D/g, '') || null,
          kcal: v.kcal ? toNum(v.kcal) : p * 4 + c * 4 + f * 9, protein: p, carbs: c, fat: f,
        },
      }));
    } catch (err) {
      showError(err);
    }
  }
  return (
    <form onSubmit={save} class="form">
      <label><span>Name</span><input value={v.name ?? ''} onInput={(e) => set('name', e.currentTarget.value)} /></label>
      <label><span>Brand</span><input value={v.brand ?? ''} onInput={(e) => set('brand', e.currentTarget.value)} /></label>
      <div class="pair">
        <label><span>Serving</span><input value={v.serving ?? ''} onInput={(e) => set('serving', e.currentTarget.value)} /></label>
        <label><span>Grams per serving</span><input inputMode="decimal" value={v.grams ?? ''} onInput={(e) => set('grams', e.currentTarget.value)} /></label>
      </div>
      <p class="muted">Per serving:</p>
      <MacroInputs v={v} set={set} />
      <label><span>Barcode (optional)</span><input inputMode="numeric" value={v.barcode ?? ''} onInput={(e) => set('barcode', e.currentTarget.value)} /></label>
      <button class="primary wide">Save and choose amount</button>
    </form>
  );
}
